import { Component, DestroyRef, ElementRef, OnInit, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { forkJoin } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import {
  SEARCH_OPS_CLUSTER_STATUSES,
  SEARCH_OPS_KEYWORD_INTENTS,
  SEARCH_OPS_KEYWORD_STATUSES,
  SEARCH_OPS_LOCALES,
  SEARCH_OPS_MAP_ROLES,
  SearchDecayItemDto,
  SearchKeywordClusterDetailDto,
  SearchKeywordClusterListItemDto,
  SearchKeywordListItemDto,
  SearchPropertyListItemDto
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-keywords',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TextareaModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.keywords.title')"
      [description]="t('growth.searchops.keywords.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.keywords.help.title'"
        [descriptionKey]="'growth.searchops.keywords.help.description'"
        [bulletKeys]="[
          'growth.searchops.keywords.help.bullets.import',
          'growth.searchops.keywords.help.bullets.map',
          'growth.searchops.keywords.help.bullets.brief'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.backlog.actions.workHome')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/backlog']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.backlog')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/urls']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.urls')"></a>
        <a pButton severity="secondary" [routerLink]="['/growth/marketing/content-calendar']" [queryParams]="{ view: 'list' }" [label]="t('growth.searchops.keywords.actions.calendar')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.fields.property') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.keywords.propertyHelp.title'"
              [descriptionKey]="'growth.searchops.keywords.propertyHelp.description'" />
          </div>
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.keywords.sections.master') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.keywords.masterHelp.title'"
              [descriptionKey]="'growth.searchops.keywords.masterHelp.description'" />
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('growth.searchops.keywords.actions.addKeyword')" (click)="keywordDialogOpen = true"></button>
            <button pButton type="button" severity="secondary" [label]="t('growth.searchops.keywords.actions.importCsv')" (click)="openCsvPicker()"></button>
            <input #csvFileInput type="file" accept=".csv,text/csv" hidden (change)="onCsvSelected($event)" />
            <button pButton type="button" severity="secondary" [label]="t('growth.searchops.keywords.actions.addMap')" (click)="mapDialogOpen = true"></button>
          </div>
          @if (importSummary()) {
            <p class="summary">{{ importSummary() }}</p>
          }
          <p-table [value]="keywords()" [paginator]="true" [rows]="20" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.phrase') }}</th>
                <th>{{ t('growth.searchops.fields.locale') }}</th>
                <th>{{ t('growth.searchops.fields.intent') }}</th>
                <th>{{ t('growth.searchops.fields.volume') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th>{{ t('growth.searchops.fields.cluster') }}</th>
                <th>{{ t('growth.searchops.fields.mappedUrl') }}</th>
                <th>{{ t('growth.searchops.fields.warning') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.phrase }}</td>
                <td>{{ row.locale }}</td>
                <td>{{ t('growth.searchops.intent.' + row.intent) }}</td>
                <td>{{ row.volume ?? '—' }}</td>
                <td>{{ t('growth.searchops.keywordStatus.' + row.status) }}</td>
                <td>{{ row.clusterName || '—' }}</td>
                <td class="wrap-url">{{ primaryUrl(row) }}</td>
                <td>
                  @if (row.cannibalizationWarning) {
                    <span class="warn">{{ t('growth.searchops.keywords.cannibalization') }}</span>
                  } @else {
                    —
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="8">{{ t('growth.searchops.keywords.empty.keywords') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.keywords.sections.clusters') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.keywords.clusterHelp.title'"
              [descriptionKey]="'growth.searchops.keywords.clusterHelp.description'" />
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('growth.searchops.keywords.actions.addCluster')" (click)="clusterDialogOpen = true"></button>
          </div>
          <p-table [value]="clusters()" [paginator]="true" [rows]="10" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.clusterName') }}</th>
                <th>{{ t('growth.searchops.fields.pillar') }}</th>
                <th>{{ t('growth.searchops.fields.contentStatus') }}</th>
                <th>{{ t('growth.searchops.fields.keywordCount') }}</th>
                <th>{{ t('growth.searchops.fields.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>
                  <button pButton type="button" class="p-button-link" [label]="row.name" (click)="openCluster(row.id)"></button>
                </td>
                <td>{{ row.pillarPhrase || '—' }}</td>
                <td>{{ t('growth.searchops.clusterStatus.' + row.contentStatus) }}</td>
                <td>{{ row.keywordCount }}</td>
                <td class="actions">
                  <button pButton type="button" severity="secondary" [label]="t('growth.searchops.keywords.actions.aceBrief')" (click)="createOrOpenBrief(row)"></button>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5">{{ t('growth.searchops.keywords.empty.clusters') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.keywords.sections.decay') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.keywords.decayHelp.title'"
              [descriptionKey]="'growth.searchops.keywords.decayHelp.description'" />
          </div>
          <p-table [value]="decay()" [paginator]="true" [rows]="10" dataKey="keywordId">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.phrase') }}</th>
                <th>{{ t('growth.searchops.fields.mappedUrl') }}</th>
                <th>{{ t('growth.searchops.fields.previousRank') }}</th>
                <th>{{ t('growth.searchops.fields.currentRank') }}</th>
                <th>{{ t('growth.searchops.fields.rankDrop') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.phrase }}</td>
                <td class="wrap-url">{{ row.targetUrl || '—' }}</td>
                <td>{{ row.previousRank ?? '—' }}</td>
                <td>{{ row.currentRank ?? '—' }}</td>
                <td>{{ row.rankDrop ?? '—' }}</td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="5">{{ t('growth.searchops.keywords.empty.decay') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>

    <p-dialog
      [header]="t('growth.searchops.keywords.dialogs.keyword')"
      [(visible)]="keywordDialogOpen"
      [modal]="true"
      [style]="{ width: '32rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.keywords.keywordDialogHelp.title'"
        [descriptionKey]="'growth.searchops.keywords.keywordDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.phrase')">
        <input pInputText [(ngModel)]="keywordModel.phrase" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.locale')">
        <p-select [options]="localeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="keywordModel.locale" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.intent')">
        <p-select [options]="intentOptions()" optionLabel="label" optionValue="value" [(ngModel)]="keywordModel.intent" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.status')">
        <p-select [options]="statusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="keywordModel.status" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.cluster')">
        <p-select [options]="clusterOptions()" optionLabel="label" optionValue="value" [(ngModel)]="keywordModel.clusterId" [showClear]="true" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.volume')">
        <p-inputNumber [(ngModel)]="keywordModel.volume" [min]="0" />
      </ain-form-field>
      <div class="check-row">
        <p-checkbox [(ngModel)]="keywordModel.brandOverlap" [binary]="true" inputId="brandOverlap" />
        <label for="brandOverlap">{{ t('growth.searchops.fields.brandOverlap') }}</label>
      </div>
      <div class="toolbar">
        <button pButton type="button" [label]="t('common.save')" [disabled]="busy()" (click)="saveKeyword()"></button>
      </div>
    </p-dialog>

    <p-dialog
      [header]="t('growth.searchops.keywords.dialogs.cluster')"
      [(visible)]="clusterDialogOpen"
      [modal]="true"
      [style]="{ width: '32rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.keywords.clusterDialogHelp.title'"
        [descriptionKey]="'growth.searchops.keywords.clusterDialogHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.clusterName')">
        <input pInputText [(ngModel)]="clusterModel.name" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.pillar')">
        <p-select [options]="keywordOptions()" optionLabel="label" optionValue="value" [(ngModel)]="clusterModel.pillarKeywordId" [showClear]="true" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.intent')">
        <p-select [options]="intentOptions()" optionLabel="label" optionValue="value" [(ngModel)]="clusterModel.intent" [showClear]="true" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.plannedUrl')">
        <input pInputText [(ngModel)]="clusterModel.plannedUrl" />
      </ain-form-field>
      <div class="toolbar">
        <button pButton type="button" [label]="t('common.save')" [disabled]="busy()" (click)="saveCluster()"></button>
      </div>
    </p-dialog>

    <p-dialog
      [header]="t('growth.searchops.keywords.dialogs.map')"
      [(visible)]="mapDialogOpen"
      [modal]="true"
      [style]="{ width: '32rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.keywords.mapHelp.title'"
        [descriptionKey]="'growth.searchops.keywords.mapHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.phrase')">
        <p-select [options]="keywordOptions()" optionLabel="label" optionValue="value" [(ngModel)]="mapModel.keywordId" appendTo="body" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.mappedUrl')">
        <input pInputText [(ngModel)]="mapModel.targetUrl" />
      </ain-form-field>
      <ain-form-field [label]="t('growth.searchops.fields.role')">
        <p-select [options]="roleOptions()" optionLabel="label" optionValue="value" [(ngModel)]="mapModel.role" appendTo="body" />
      </ain-form-field>
      <div class="toolbar">
        <button pButton type="button" [label]="t('common.save')" [disabled]="busy()" (click)="saveMap()"></button>
      </div>
    </p-dialog>

    <p-dialog
      [header]="clusterDialogTitle()"
      [(visible)]="clusterDetailOpen"
      [modal]="true"
      [style]="{ width: '48rem' }"
      appendTo="body">
      @if (clusterDetail(); as detail) {
        <ain-info-hint
          [titleKey]="'growth.searchops.keywords.clusterDetailHelp.title'"
          [descriptionKey]="'growth.searchops.keywords.clusterDetailHelp.description'" />
        @if (detail.cannibalizationWarning) {
          <p class="warn">{{ t('growth.searchops.keywords.cannibalization') }}</p>
        }
        <p>{{ t('growth.searchops.fields.pillar') }}: {{ detail.cluster.pillarPhrase || '—' }}</p>
        <p>{{ t('growth.searchops.fields.contentStatus') }}: {{ t('growth.searchops.clusterStatus.' + detail.cluster.contentStatus) }}</p>
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.searchops.keywords.actions.aceBrief')" (click)="createOrOpenBrief(detail.cluster)"></button>
          @if (detail.cluster.aceBriefId) {
            <a pButton severity="secondary" [routerLink]="['/growth/marketing/ace-studio/briefs', detail.cluster.aceBriefId]" [label]="t('growth.searchops.keywords.actions.openAceBrief')"></a>
          }
          <a pButton severity="secondary" [routerLink]="['/growth/marketing/article-publishing']" [label]="t('growth.searchops.keywords.actions.articles')"></a>
        </div>
        <p-table [value]="detail.keywords" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.searchops.fields.phrase') }}</th>
              <th>{{ t('growth.searchops.fields.intent') }}</th>
              <th>{{ t('growth.searchops.fields.mappedUrl') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.phrase }}</td>
              <td>{{ t('growth.searchops.intent.' + row.intent) }}</td>
              <td class="wrap-url">{{ primaryUrl(row) }}</td>
            </tr>
          </ng-template>
        </p-table>
      }
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .section-title h3 { margin: 0; font-size: 1rem; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .check-row { display: flex; align-items: center; gap: 8px; margin: 6px 0; }
    .summary { color: #64748b; }
    .warn { color: #9a3412; font-weight: 600; }
    .wrap-url { overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsKeywordsComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;
  readonly csvFileInput = viewChild<ElementRef<HTMLInputElement>>('csvFileInput');

  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly importSummary = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly keywords = signal<SearchKeywordListItemDto[]>([]);
  readonly clusters = signal<SearchKeywordClusterListItemDto[]>([]);
  readonly decay = signal<SearchDecayItemDto[]>([]);
  readonly clusterDetail = signal<SearchKeywordClusterDetailDto | null>(null);

  selectedPropertyId: string | null = null;
  keywordDialogOpen = false;
  clusterDialogOpen = false;
  mapDialogOpen = false;
  clusterDetailOpen = false;
  keywordModel = { phrase: '', locale: 'en-US', intent: 'informational', status: 'track', clusterId: null as string | null, volume: null as number | null, brandOverlap: false };
  clusterModel = { name: '', pillarKeywordId: null as string | null, intent: null as string | null, plannedUrl: '' };
  mapModel = { keywordId: '', targetUrl: '', role: 'primary' };
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthSearchOpsChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
      const clusterId = params.get('clusterId');
      if (clusterId) {
        this.openCluster(clusterId);
      }
    });
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  clusterDialogTitle(): string {
    return this.clusterDetail()?.cluster.name || this.t('growth.searchops.keywords.dialogs.clusterDetail');
  }

  openCsvPicker(): void {
    this.csvFileInput()?.nativeElement.click();
  }

  propertyQuery(): Record<string, string> {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  propertyOptions(): Array<{ label: string; value: string }> {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  localeOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_LOCALES.map(value => ({ value, label: value }));
  }

  intentOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_KEYWORD_INTENTS.map(value => ({ value, label: this.t('growth.searchops.intent.' + value) }));
  }

  statusOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_KEYWORD_STATUSES.map(value => ({ value, label: this.t('growth.searchops.keywordStatus.' + value) }));
  }

  roleOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_MAP_ROLES.map(value => ({ value, label: this.t('growth.searchops.mapRole.' + value) }));
  }

  clusterOptions(): Array<{ label: string; value: string }> {
    return this.clusters().map(item => ({ value: item.id, label: item.name }));
  }

  keywordOptions(): Array<{ label: string; value: string }> {
    return this.keywords().map(item => ({ value: item.id, label: `${item.phrase} (${item.locale})` }));
  }

  primaryUrl(row: SearchKeywordListItemDto): string {
    const primary = row.maps.find(item => item.role === 'primary') ?? row.maps[0];
    return primary?.targetUrl || '—';
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties.length > 0) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  saveKeyword(): void {
    if (!this.selectedPropertyId || !this.keywordModel.phrase.trim()) {
      return;
    }
    this.busy.set(true);
    this.api.upsertKeyword(this.selectedPropertyId, {
      phrase: this.keywordModel.phrase,
      locale: this.keywordModel.locale,
      intent: this.keywordModel.intent,
      status: this.keywordModel.status,
      clusterId: this.keywordModel.clusterId,
      volume: this.keywordModel.volume,
      brandOverlap: this.keywordModel.brandOverlap
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.keywordDialogOpen = false;
        this.keywordModel = { phrase: '', locale: 'en-US', intent: 'informational', status: 'track', clusterId: null, volume: null, brandOverlap: false };
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveCluster(): void {
    if (!this.selectedPropertyId || !this.clusterModel.name.trim()) {
      return;
    }
    this.busy.set(true);
    this.api.createCluster(this.selectedPropertyId, {
      name: this.clusterModel.name,
      pillarKeywordId: this.clusterModel.pillarKeywordId,
      intent: this.clusterModel.intent,
      plannedUrl: this.clusterModel.plannedUrl || null,
      contentStatus: SEARCH_OPS_CLUSTER_STATUSES[0]
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.clusterDialogOpen = false;
        this.clusterModel = { name: '', pillarKeywordId: null, intent: null, plannedUrl: '' };
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  saveMap(): void {
    if (!this.mapModel.keywordId || !this.mapModel.targetUrl.trim()) {
      return;
    }
    this.busy.set(true);
    this.api.upsertMap({
      keywordId: this.mapModel.keywordId,
      targetUrl: this.mapModel.targetUrl,
      role: this.mapModel.role
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: mapped => {
        this.mapDialogOpen = false;
        if (mapped.cannibalizationWarning) {
          this.error.set(this.t('growth.searchops.keywords.cannibalization'));
        }
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  onCsvSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.selectedPropertyId) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const csv = String(reader.result ?? '');
      this.busy.set(true);
      this.api.importKeywords(this.selectedPropertyId!, csv).pipe(finalize(() => this.busy.set(false))).subscribe({
        next: result => {
          this.importSummary.set(this.t('growth.searchops.keywords.importResult')
            .replace('{created}', String(result.created))
            .replace('{updated}', String(result.updated)));
          this.loadBoard();
        },
        error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
      });
    };
    reader.readAsText(file);
  }

  openCluster(id: string): void {
    this.api.getCluster(id).subscribe({
      next: detail => {
        this.clusterDetail.set(detail);
        this.clusterDetailOpen = true;
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  createOrOpenBrief(cluster: SearchKeywordClusterListItemDto): void {
    if (cluster.aceBriefId) {
      void this.router.navigate(['/growth/marketing/ace-studio/briefs', cluster.aceBriefId]);
      return;
    }
    this.busy.set(true);
    this.api.createAceBrief(cluster.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: result => {
        void this.router.navigate(['/growth/marketing/ace-studio/briefs', result.briefId]);
      },
      error: err => this.error.set(err?.status === 403
        ? this.t('growth.searchops.keywords.messages.aceForbidden')
        : (err?.error?.error ?? this.t('growth.searchops.messages.saveFailed')))
    });
  }

  private loadBoard(): void {
    if (!this.selectedPropertyId) {
      this.keywords.set([]);
      this.clusters.set([]);
      this.decay.set([]);
      return;
    }
    forkJoin({
      keywords: this.api.listKeywords(this.selectedPropertyId),
      clusters: this.api.listClusters(this.selectedPropertyId),
      decay: this.api.listDecay(this.selectedPropertyId)
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: board => {
        this.keywords.set(board.keywords);
        this.clusters.set(board.clusters);
        this.decay.set(board.decay);
        if (this.mapModel.keywordId === '' && board.keywords.length > 0) {
          this.mapModel.keywordId = board.keywords[0].id;
        }
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
